import { z } from "zod";

/**
 * Teams Manifest Response Schema
 *
 * Returns the Microsoft Teams app manifest JSON for creating a Teams app from a deployment.
 * This manifest follows Microsoft Teams schema v1.16.
 *
 * @see https://developer.microsoft.com/en-us/json-schemas/teams/v1.16/MicrosoftTeams.schema.json
 */
export const TeamsManifestResponseSchema = z.object({
  $schema: z.string().url(),
  manifestVersion: z.string(),
  version: z.string(),
  id: z.string().uuid(),
  packageName: z.string(),
  developer: z.object({
    name: z.string(),
    websiteUrl: z.string().url(),
    privacyUrl: z.string().url(),
    termsOfUseUrl: z.string().url()
  }),
  icons: z.object({
    color: z.string(),
    outline: z.string()
  }),
  name: z.object({
    short: z.string().max(30),
    full: z.string().max(100)
  }),
  description: z.object({
    short: z.string().max(80),
    full: z.string().max(4000)
  }),
  accentColor: z.string(),
  bots: z.array(
    z.object({
      botId: z.string(),
      scopes: z.array(z.enum(["personal", "team", "groupchat"])),
      supportsFiles: z.boolean().optional(),
      isNotificationOnly: z.boolean().optional()
    })
  ),
  composeExtensions: z.array(z.unknown()),
  configurableTabs: z.array(z.unknown()),
  staticTabs: z.array(z.unknown()),
  permissions: z.array(z.string()),
  validDomains: z.array(z.string())
});

export type TeamsManifestResponse = z.infer<typeof TeamsManifestResponseSchema>;

/**
 * Get Teams Manifest Path Variables Schema
 */
export const GetTeamsManifestPathVarsSchema = z.object({
  id: z.string().uuid()
});

export type GetTeamsManifestPathVars = z.infer<typeof GetTeamsManifestPathVarsSchema>;

/**
 * Teams Manifest Preview Response Schema
 *
 * Returns a preview of the manifest data with editable fields and validation warnings.
 * Used by the pre-publish confirmation modal to show what will be published.
 */
export const TeamsManifestPreviewResponseSchema = z.object({
  shortName: z.string(),
  fullName: z.string(),
  shortDescription: z.string(),
  fullDescription: z.string(),
  iconUrl: z.string().nullable(),
  warnings: z.array(z.string())
});

export type TeamsManifestPreviewResponse = z.infer<typeof TeamsManifestPreviewResponseSchema>;

/**
 * Get Teams Manifest Preview Path Variables Schema
 */
export const GetTeamsManifestPreviewPathVarsSchema = z.object({
  id: z.string().uuid()
});

export type GetTeamsManifestPreviewPathVars = z.infer<typeof GetTeamsManifestPreviewPathVarsSchema>;
